/**
 * Plans API service for fetching subscription plan data.
 * Public endpoint for plan listing, admin endpoints for CRUD operations.
 * Plans are stored in DynamoDB (SubscriptionPlansConfigTable).
 */

import type { Plan, SavePlanRequest } from '@/types/plans';
import { BaseApiService } from './BaseApiService';
import { API_URL, PUBLIC_API_URL } from '@/lib/apiConfig';

class PlansApiService extends BaseApiService {
  private publicBaseUrl: string;

  constructor() {
    super(API_URL, 'PlansApi');
    this.publicBaseUrl = PUBLIC_API_URL;
  }

  /**
   * List all active plans (public, no auth required).
   */
  async listPlans(): Promise<Plan[]> {
    this.log.debug('Listing plans', { action: 'listPlans' });

    const response = await fetch(`${this.publicBaseUrl}/plans`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list plans', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list plans');
    }

    const data = await response.json();
    this.log.info('Plans listed', { action: 'listPlans', count: data.plans?.length || 0 });
    return data.plans || [];
  }

  /**
   * List all plans including inactive (admin only).
   */
  async adminListPlans(): Promise<Plan[]> {
    this.log.debug('Admin listing plans', { action: 'adminListPlans' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/plans`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to admin list plans', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list plans');
    }

    const data = await response.json();
    this.log.info('Admin plans listed', { action: 'adminListPlans', count: data.plans?.length || 0 });
    return data.plans || [];
  }

  /**
   * Get a specific plan (admin only).
   */
  async getPlan(planId: string): Promise<Plan> {
    this.log.debug('Getting plan', { action: 'getPlan', planId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/plans/${planId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get plan', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get plan');
    }

    const data = await response.json();
    return data.plan;
  }

  /**
   * Create a new plan (admin only).
   */
  async createPlan(request: SavePlanRequest): Promise<Plan> {
    this.log.debug('Creating plan', { action: 'createPlan', planId: request.planId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/plans`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create plan', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create plan');
    }

    const data = await response.json();
    return data.plan;
  }

  /**
   * Update an existing plan (admin only).
   */
  async updatePlan(planId: string, request: Partial<SavePlanRequest>): Promise<Plan> {
    this.log.debug('Updating plan', { action: 'updatePlan', planId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/plans/${planId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update plan', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update plan');
    }

    const data = await response.json();
    return data.plan;
  }

  /**
   * Delete a plan (admin only).
   */
  async deletePlan(planId: string): Promise<void> {
    this.log.debug('Deleting plan', { action: 'deletePlan', planId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/admin/plans/${planId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete plan', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete plan');
    }

    this.log.info('Plan deleted', { action: 'deletePlan', planId });
  }
}

export const plansApi = new PlansApiService();
export default plansApi;
